import lowerCase from 'lodash/lowerCase';
import startCase from 'lodash/startCase';

import { USER_SESSION_INFO } from 'constants/cookieConstants';
import { RESPONSE_STATUS_CODES, DATE_FORMATS, SORT_OPTION_TYPES } from 'constants/index';

import { getCookiesOnServer } from 'utils/auth';
import { createDate } from 'utils/date';
import { dehydrate, QueryClient } from 'utils/react-query';

import { getRedirectPageRoute } from 'containers/Globals/queries';

import { getServerLanguage } from 'src/i18n';

import {
  ROUTE_RESERVED_KEYWORDS,
  FILTER_TYPES,
  ENTITY_TYPES,
  PAGE_CATEGORIES,
  RENDERING_STATUSES,
} from 'operabase-router/constants';

import QUERIES from './queries';

const getTime = () => Date.now();

const getIndexSSRQueries = (context = {}, extras = {}) => {
  const { basePath, linkProps = {}, page } = context;
  const { country } = extras;
  const isProductionListing =
    page?.entityType === ENTITY_TYPES.PRODUCTION && page?.category === PAGE_CATEGORIES.LISTING;
  const { filters = {} } = linkProps;

  if (isProductionListing) {
    return [QUERIES.PRODUCTIONS.GET_PRODUCTION_CREDITS({ limit: 10, enabled: true })];
  }

  switch (basePath) {
    case ROUTE_RESERVED_KEYWORDS.composers: {
      return [QUERIES.GLOBAL.GET_COMPOSER_LIST({ query: filters?.[FILTER_TYPES.QUERY] })];
    }

    case ROUTE_RESERVED_KEYWORDS.seasons: {
      if (country) {
        const years = context?.context?.years || context?.context?.year || [];
        const season_id = years?.[0]?.id || createDate().format(DATE_FORMATS.YEAR);

        const apiFilters = {
          has_productions: 'yes',
          sort: SORT_OPTION_TYPES.CITY_ASC.value,
          ...(country?.id && { country_id: country?.id }),
          ...(season_id && { season_id }),
        };

        return [QUERIES.ORGANIZATIONS.GET_ORGANIZATIONS({ filters: apiFilters, limit: 12, infinite: true })];
      }

      const years = context?.context?.years || context?.context?.year || [];
      const season_id = years?.[0]?.id || createDate().format(DATE_FORMATS.YEAR);

      return [QUERIES.PRODUCTIONS.GET_COUNTRIES({ filters: { season_id } })];
    }

    default:
      return [];
  }
};

export const withPerformanceMeta =
  fn =>
  (...args) => {
    const start = getTime();
    const promise = fn(...args);

    const calcElapsedTime = data => {
      const { serverTiming, ...rest } = data || {};

      return {
        ...rest,
        __performance__: {
          serverTiming,
          responseTime: getTime() - start,
          size: `${(new TextEncoder().encode(JSON.stringify({ ...data })).length / 1024).toFixed(2)}kB`,
        },
      };
    };

    return promise.then(calcElapsedTime, calcElapsedTime);
  };

export const getEntityDetails = (
  { entityType, entityId, showApprovedHidden, asEdit, program_published, queryConfig },
  cookies,
  language,
) => {
  if (!entityId) {
    return {};
  }

  const { queryFn, getProps = () => {} } = QUERIES.GLOBAL.GET_ENTITY_DETAILS({
    entityType,
    entityId,
    showApprovedHidden,
    asEdit,
    program_published,
    queryConfig,
  });

  return queryFn(getProps(), cookies, language);
};

export const fetchQueryPromises = (
  queryClient,
  queryList,
  { context = {}, extraParams = {}, cookies, language, prefetch = true },
) => {
  const promises = queryList?.reduce((queries, queryInfo) => {
    let queryObj = queryInfo;

    if (typeof queryInfo === 'function') {
      queryObj = queryInfo({});
    }

    const isEnabled = queryObj?.isEnabled ? queryObj.isEnabled({ context, extras: extraParams }) : true;

    if (isEnabled) {
      const { queryFn, getProps, infinite = false } = queryObj;
      const params = getProps ? getProps({ context, extras: extraParams }) : {};
      const getData = queryFn(params, cookies, language);

      const queryProps = { queryKey: getData.queryKey, queryFn: withPerformanceMeta(getData.queryFn) };

      if (infinite) {
        if (prefetch) {
          queries.push(queryClient.prefetchInfiniteQuery(queryProps));
        } else {
          queries.push(queryClient.fetchInfiniteQuery(queryProps));
        }
      } else if (prefetch) {
        queries.push(queryClient.prefetchQuery(queryProps));
      } else {
        queries.push(queryClient.fetchQuery(queryProps));
      }
    }

    return queries;
  }, []);

  return promises || [];
};

export const fetchPrerequisiteQueries = async (queryClient, queries, { context, extraParams, cookies, language }) => {
  const { queryList, queryProcessor } = queries.reduce(
    (acc, entry) => {
      acc.queryList.push(entry?.query);
      acc.queryProcessor.push({
        key: entry?.key,
        select: entry?.select,
      });

      return acc;
    },
    { queryList: [], queryProcessor: [] },
  );

  const promises = fetchQueryPromises(queryClient, queryList, {
    context,
    extraParams,
    cookies,
    language,
    prefetch: false,
  });

  const responses = await Promise.allSettled(promises).then(res => ({
    ...res.reduce((acc, entry, index) => {
      const processor = queryProcessor[index];
      const selector = processor?.select ? processor?.select : response => response;

      return {
        ...acc,
        [processor?.key]: selector(entry?.value || null),
      };
    }, {}),
  }));

  return responses;
};

const getServerTiming = (ctx, { queries, prefetchQueryTime }) => {
  const serverTiming = ctx.res.getHeader('Server-Timing');

  const queryMetrics = queries?.reduce((acc, query, index) => {
    const data = query?.state?.data;
    // eslint-disable-next-line no-underscore-dangle
    const performanceInfo = data?.__performance__ || data?.pages?.[0]?.__performance__ || {};
    const { responseTime = 0, serverTiming: queryServerTiming } = performanceInfo;
    const queryKey = query?.queryKey?.[1]?.toLowerCase();
    const descriptionText = startCase(lowerCase(queryKey));
    let apiMetrics = [];

    if (queryServerTiming) {
      const metrics = queryServerTiming.split(',');
      apiMetrics = metrics.map((metric, idx) => {
        const [apiName, ...rest] = metric.trim().split(';');
        const result = {
          name: `2.${index + 1}.${idx + 1}-${queryKey}-${apiName}`,
          description: `API: ${descriptionText}`,
        };

        rest.forEach(part => {
          const [key, value] = part.trim().split('=');

          if (key === 'dur') {
            result.duration = parseFloat(value);
          }
        });

        return result;
      });

      acc.push(...apiMetrics);
    }

    acc.push({
      name: `2.${index + 1}.${apiMetrics.length + 1}-${queryKey}`,
      description: `API (F): ${descriptionText}`,
      duration: responseTime,
    });

    return acc;
  }, []);

  const metrics = [
    {
      name: '2-getServerProps',
      description: 'getServerSideProps',
      duration: prefetchQueryTime,
    },
    ...queryMetrics,
  ];

  const additionalServerTiming = metrics
    .map(metric => `${metric.name};desc="${metric.description}";dur=${metric.duration}`)
    .join(',');

  return `${serverTiming ? `${serverTiming},` : ''}${additionalServerTiming}`;
};

const getServerProps = async ctx => {
  const start = getTime();
  const { req } = ctx;

  const userAgent = req.headers['user-agent'];
  const userSessionInfo = req?.session?.get(USER_SESSION_INFO);
  const { user = null, isLoggedIn = false } = userSessionInfo || {};
  const isNextDataRequest = req.nextdata || false;
  const shouldRenderSSR = [RENDERING_STATUSES.ALLOW, RENDERING_STATUSES.OVERRIDE].includes(req.trustInfo?.status);

  const language = getServerLanguage(req);
  const cookies = getCookiesOnServer(ctx);
  const context = JSON.parse(JSON.stringify(req.context || ''));
  const { query, page } = context || {};
  const { entityType, edit = false, pro = false } = page || {};
  const entityId = page?.entity?.id;
  const { program_published } = query || {};

  let entity = null;
  const selectedSeasonKey = null;
  let statusCode = context.status || RESPONSE_STATUS_CODES.NOT_FOUND;
  let redirect = [RESPONSE_STATUS_CODES.TEMPORARY_REDIRECT, RESPONSE_STATUS_CODES.PERMANENT_REDIRECT].includes(
    statusCode,
  );
  let location = redirect && context.redirect ? context.redirect : null;

  const queryClient = new QueryClient();

  const baseUserQueries = [];

  if (shouldRenderSSR && isLoggedIn && !isNextDataRequest) {
    baseUserQueries.push({
      query: QUERIES.ACCOUNTS.GET_USER_PROFILES,
      key: 'userProfiles',
      select: response => response?.data,
    });
  }

  const { userProfiles } = await fetchPrerequisiteQueries(queryClient, baseUserQueries, {
    context,
    extraParams: { user },
    cookies,
    language,
  });

  const dependentUserQueries = [];

  if (shouldRenderSSR && isLoggedIn && !isNextDataRequest) {
    const userQueries = [
      QUERIES.ACCOUNTS.GET_USER_DETAILS,
      QUERIES.ACCOUNTS.GET_USER_PERMISSIONS,
      QUERIES.ACCOUNTS.GET_USER_SUBSCRIPTIONS,
      QUERIES.ACCOUNTS.GET_ACTIVE_PROFILE_SUBSCRIPTIONS,
    ];

    dependentUserQueries.push(...userQueries);
  }

  if ((!edit && entityType && entityId) || entityType === ENTITY_TYPES.AGENCY) {
    const showApprovedHidden = false; // TODO: FB-000000 Solve for SSR user permissions

    const { queryKey, queryFn } = getEntityDetails(
      { entityType, entityId, showApprovedHidden, asEdit: edit, program_published },
      cookies,
      language,
    );

    entity = queryKey && (await queryClient.fetchQuery(queryKey, withPerformanceMeta(queryFn)));
  }

  if (statusCode === RESPONSE_STATUS_CODES.SUCCESS) {
    if (entityType && entityId && entity?.id !== entityId) {
      const newRoute =
        entityId &&
        entityType &&
        (await getRedirectPageRoute({ entityType, entityId, url: req?.url, language }, cookies, language));

      if (newRoute) {
        redirect = true;
        statusCode = RESPONSE_STATUS_CODES.PERMANENT_REDIRECT;
        location = newRoute;

        if (pro && entityType === ENTITY_TYPES.PROFILE) {
          location = `${ROUTE_RESERVED_KEYWORDS.casting}${location}`;
        }
      }
    } else if (shouldRenderSSR) {
      const extraParams = {
        user,
        entity,
        isLoggedIn,
        selectedSeasonKey,
        userProfiles,
      };
      const pageQueryList = entityType && entityId ? [] : getIndexSSRQueries(context, extraParams);
      const queryList = [QUERIES.GLOBAL.GET_HOME_NUMBERS, ...dependentUserQueries, ...pageQueryList];

      if (queryList?.length > 0) {
        const promises = fetchQueryPromises(queryClient, queryList, {
          context,
          extraParams,
          cookies,
          language,
          prefetch: true,
        });

        await Promise.allSettled(promises);
      }
    }
  }

  const dehydratedState = JSON.parse(JSON.stringify(dehydrate(queryClient))); // NOTE: Its a fix for variation for data structure support from API

  let redirectProps = null;

  if (redirect) {
    redirectProps = {
      destination: location,
      permanent: statusCode === RESPONSE_STATUS_CODES.PERMANENT_REDIRECT,
      statusCode,
    };
  }

  const prefetchQueryTime = getTime() - start;

  const serverTiming = getServerTiming(ctx, { queries: dehydratedState?.queries, prefetchQueryTime });
  ctx.res.setHeader('Server-Timing', serverTiming);
  ctx.res.statusCode = statusCode;

  return {
    notFound: statusCode === RESPONSE_STATUS_CODES.NOT_FOUND,
    context: {
      entity,
      ...context,
    },
    ...(redirect &&
      !req.nextdata && {
        redirect: redirectProps,
      }),
    props: {
      statusCode,
      redirect: redirectProps,
      prefetchQueryTime,
      userAgent,
      dehydratedState,
    },
  };
};

export default getServerProps;
